import { Autocomplete, FormControl, InputLabel, Select, TextField } from '@mui/material'
import type { SelectChangeEvent } from '@mui/material'
import React from 'react'
import { lookupName, useLookupField } from '../utils/lookups'
import type { LookupFieldOptions, LookupOption } from '../utils/lookups'

/** Campos `outlined` de las barras de filtros (patrón de los listados). */
export const filterSx = {
    '& .MuiInputBase-root': {
        backgroundColor: 'rgb(var(--k-surface))',
        color: 'rgb(var(--k-ink))',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgb(var(--k-line-strong))',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgb(var(--k-muted))',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgb(var(--k-primary))',
    },
    '& .MuiInputLabel-root': {
        color: 'rgb(var(--k-muted))',
    },
    '& .MuiInputLabel-root.Mui-focused': {
        color: 'rgb(var(--k-primary-ink))',
    },
    '& .MuiSvgIcon-root': {
        color: 'rgb(var(--k-muted))',
    },
    '& .MuiInputBase-input': {
        color: 'rgb(var(--k-ink))',
    },
} as const

export const filterMenuProps = {
    slotProps: {
        paper: { className: 'v2-select-menu' },
    },
}

export const LookupField = <T extends LookupOption>({ label, ...options }: LookupFieldOptions<T> & { label: string }) => {
    const lookup = useLookupField(options)
    return (
        <Autocomplete<T>
            size="small"
            sx={{ ...filterSx, width: '100%', minWidth: 280, maxWidth: 420, flex: '1 1 320px' }}
            options={lookup.options}
            loading={lookup.searching}
            value={options.value}
            inputValue={lookup.inputValue}
            onInputChange={(_, next, reason) => lookup.changeInput(next, reason)}
            onChange={(_, option) => options.onChange(option)}
            getOptionLabel={(option) => (option ? lookupName(option) : '')}
            isOptionEqualToValue={(option, current) => option?.id === current?.id}
            filterOptions={(items) => items}
            noOptionsText={lookup.noOptionsText}
            loadingText="Buscando..."
            slotProps={{ paper: { className: 'v2-select-menu' } }}
            renderInput={(params) => (
                <TextField {...params} label={label} variant="outlined" size="small" />
            )}
        />
    )
}

export interface FilterSelectProps {
    label: string
    value: string
    onChange: (event: SelectChangeEvent<string>) => void
    minWidth?: number
    children: React.ReactNode
}

export const FilterSelect = ({ label, value, onChange, minWidth = 140, children }: FilterSelectProps) => (
    <FormControl size="small" variant="outlined" sx={{ ...filterSx, minWidth, width: { xs: '100%', sm: 'auto' } }}>
        <InputLabel>{label}</InputLabel>
        <Select label={label} value={value} onChange={onChange} MenuProps={filterMenuProps}>
            {children}
        </Select>
    </FormControl>
)
